/** Umami tracking utilities — null-safe (no-op when Umami is not loaded) */

type TrackData = Record<string, string | number | boolean>

interface UmamiTracker {
  track: (name: string, data?: TrackData) => void
  identify: (data: TrackData) => void
}

declare global {
  interface Window {
    umami?: UmamiTracker
  }
}

// The Umami script loads after an admin-IP check (main.tsx), so a page entered
// directly fires its mount events before `window.umami` exists. Measured on prod
// (90 days to 2026-10-12): only 12 of 62 landings on a course page recorded
// `learn:course:view`, against 195 of 196 in-app navigations. Calls made before
// the script loads wait here and are replayed once it does.
type Pending = { kind: 'track'; name: string; data?: TrackData } | { kind: 'identify'; data: TrackData }

// A landing fires a handful of events; the cap only bounds a script that never
// arrives without anyone calling disableTracking().
const MAX_PENDING = 50

let pending: Pending[] = []
let disabled = false

function enqueue(call: Pending): void {
  if (disabled) return
  if (pending.length >= MAX_PENDING) pending.shift()
  pending.push(call)
}

export function trackEvent(name: string, data?: TrackData): void {
  if (window.umami) window.umami.track(name, data)
  else enqueue({ kind: 'track', name, data })
}

export function identifyUser(userId: string, email: string): void {
  const data = { userId, email }
  if (window.umami) window.umami.identify(data)
  else enqueue({ kind: 'identify', data })
}

/**
 * Replay the calls made before the script loaded, in order. Call it from the
 * script's `load` event. A replayed event carries the URL of the page at replay
 * time, which on a landing is still the page that fired it.
 */
export function flushTracking(): void {
  const umami = window.umami
  if (!umami) return
  const queued = pending
  pending = []
  for (const call of queued) {
    if (call.kind === 'track') umami.track(call.name, call.data)
    else umami.identify(call.data)
  }
}

/** Umami will not load for this page view (panel user, script error, no config): drop and stop queueing. */
export function disableTracking(): void {
  disabled = true
  pending = []
}
